import React, { useEffect, useRef, useState } from "react";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { Button, Caption1, Dialog, Input } from "@activecollab/components";
import { Form, Formik } from "formik";
import PropTypes from "prop-types";
import * as yup from "yup";
import { MEMBER_PERMISSIONS } from "~/records/record.project";
import { fetchProjectsDetails } from "~/store/actions/actions.projects";
import { getLoggedUser } from "~/store/selectors/selector.users";
import { isMemberWithoutPermissions } from "~/utils/userHelper";
import { CustomFormField } from "../CustomFormField";
import { ProjectPickerWithFavorites } from "../project/ProjectPickerWithFavorites";

export const createTaskValidator = yup.object().shape({
  name: yup.string().required().max(191),
  project_id: yup.number().required(),
});

export const CreateTaskDialog = ({ open, onCancel, onSubmit, name, explainerText, projectId }) => {
  const { t } = useTranslation();
  const formikRef = useRef();
  const dispatch = useDispatch();
  const [projects, setProjects] = useState({});
  const [isFetching, setIsFetching] = useState(true);
  const loggedUser = useSelector(getLoggedUser);

  useEffect(() => {
    dispatch(fetchProjectsDetails()).then((data) => {
      setIsFetching(false);
      const { response = {} } = data;
      const { entities = {} } = response;
      const { projects: userProjects = {} } = entities;
      const filteredProjects = Object.values(userProjects).reduce((acc, project) => {
        if (
          !isMemberWithoutPermissions(loggedUser) ||
          project.member_permissions === MEMBER_PERMISSIONS.COLLABORATIVE ||
          project.leader_id === loggedUser.id
        ) {
          acc[project.id] = project.name;
        }
        return acc;
      }, {});

      setProjects(filteredProjects);
    });
  }, [dispatch, loggedUser]);

  const handleDialogSubmit = useCallback(
    (values) => {
      if (typeof onSubmit === "function") {
        onSubmit(values);
      }
    },
    [onSubmit]
  );

  const onKeyDown = useCallback((e) => {
    if (e.key === "Enter") {
      formikRef.current?.handleSubmit();
    }
  }, []);

  return (
    <Dialog
      open={open}
      onClose={onCancel}
    >
      <Formik
        innerRef={formikRef}
        initialValues={{
          name,
          project_id: projectId || null,
        }}
        validationSchema={createTaskValidator}
        onSubmit={handleDialogSubmit}
        validateOnMount
      >
        {({ handleChange, handleBlur, values, isSubmitting, isValid }) => (
          <Form>
            <Dialog.Title>{t("Create Task")}</Dialog.Title>
            <Dialog.ContentDivider />
            <Dialog.Content>
              <Caption1
                weight="bold"
                color="secondary"
                className="tw-mb-1"
              >
                {t("Project")}
              </Caption1>
              <CustomFormField name="project_id">
                {({ field }) => (
                  <ProjectPickerWithFavorites
                    loading={isFetching}
                    selected={field.value}
                    projects={projects}
                    onChange={field.onChange}
                    placeholder={`${t("Select project")}...`}
                    noResultText={t("No result")}
                    autoFocus={values.project_id === null}
                  />
                )}
              </CustomFormField>
              <Caption1
                weight="bold"
                color="secondary"
                className="tw-mb-1 tw-mt-8"
              >
                {t("Task Name")}
              </Caption1>
              <Input
                name="name"
                className="tw-w-full"
                onChange={handleChange}
                onBlur={handleBlur}
                value={values.name}
                placeholder={t("Task Name")}
                autoFocus={values.project_id !== null}
                onKeyDown={onKeyDown}
              />
              {explainerText.length ? (
                <Caption1
                  color="tertiary"
                  className="tw-mt-2"
                >
                  {explainerText}
                </Caption1>
              ) : null}
            </Dialog.Content>
            <Dialog.ContentDivider />
            <Dialog.Actions>
              <Button
                disabled={isSubmitting || !isValid}
                type="submit"
                variant="primary"
                data-testid="rename-dialog-submit"
                data-qa-id="confirm-modal-submit"
                className="tw-mr-3"
              >
                {t("Create")}
              </Button>
              <Button
                disabled={isSubmitting}
                type="button"
                variant="secondary"
                onClick={onCancel}
              >
                {t("Cancel")}
              </Button>
            </Dialog.Actions>
          </Form>
        )}
      </Formik>
    </Dialog>
  );
};

CreateTaskDialog.propTypes = {
  open: PropTypes.bool,
  onCancel: PropTypes.func,
  onSubmit: PropTypes.func,
  name: PropTypes.string,
  explainerText: PropTypes.string,
  projectId: PropTypes.number,
};

CreateTaskDialog.defaultProps = {
  open: false,
  onCancel: () => null,
  onSubmit: () => null,
  handleChange: () => null,
};
